import type { Database } from '../lib/adatbazis.ts';

export type RegSor = Database['public']['Tables']['iroda_regisztraciok']['Row'];
export type RegisztracioAllapot = 'megerosites_var' | 'fuggo' | 'jovahagyva' | 'elutasitva';

export const ALLAPOT_CIMKE: Record<RegisztracioAllapot, string> = {
  megerosites_var: 'Megerősítésre vár',
  fuggo: 'Függő',
  jovahagyva: 'Jóváhagyva',
  elutasitva: 'Elutasítva',
};

/** A fülek sorrendje a listán; az első az alapértelmezett. */
export const FULEK: RegisztracioAllapot[] = ['fuggo', 'megerosites_var', 'jovahagyva', 'elutasitva'];

export function allapotE(s: string | null): s is RegisztracioAllapot {
  return s !== null && Object.hasOwn(ALLAPOT_CIMKE, s);
}

/**
 * A lekérdezés oszlopai. A `megerosito_token_hash` oszlophoz nincs SELECT-jog (ezért a `*` sem használható).
 * Egyetlen literál, hogy a típusgenerátor a sorok típusát ebből tudja levezetni.
 */
export const REGISZTRACIO_OSZLOPOK = 'id, allapot, iroda_nev, halozat_kulcs, halozat_egyeb, halozati_nyilatkozat, iroda_telepules, iroda_regio, iroda_cim, ceg_forma, cegnev, szekhely, adoszam, cegjegyzekszam, ev_nyilvantartasi_szam, szamlazasi_email, kapcsolattarto_nev, kapcsolattarto_email, kapcsolattarto_telefon, megjegyzes, aszf_verzio, aszf_hash, adatvedelem_verzio, adatvedelem_hash, kepviseleti_nyilatkozat, elfogadva_at, ip_hash, user_agent, megerosites_lejar, email_megerositve_at, dontes_at, dontes_szerzo, elutasitas_indok, office_id, meghivas_allapot, meghivas_hiba, iroda_torolve_at, created_at, updated_at' as const;

export function szuresAllapotra<T extends { allapot: string }>(sorok: T[], allapot: RegisztracioAllapot): T[] {
  return sorok.filter((s) => s.allapot === allapot);
}

/** A hálózat olvasható neve a regisztrációs sorból. */
export function halozatNev(r: Pick<RegSor, 'halozat_kulcs' | 'halozat_egyeb'>): string {
  if (r.halozat_kulcs === 'duna_house') return 'Duna House';
  if (r.halozat_kulcs === 'otthon_centrum') return 'Otthon Centrum';
  return r.halozat_egyeb ? `${r.halozat_egyeb} (egyéb hálózat)` : 'Független';
}

/**
 * A super admin figyelmeztetései a döntés előtt (nem tiltanak, a döntés az övé): azonos adószámú másik
 * függő/jóváhagyott regisztráció, már létező profil a kapcsolattartó címén, foglalt slug.
 */
export function figyelmeztetesek(
  r: Pick<RegSor, 'id' | 'adoszam'>,
  tobbi: { adoszam: string; allapot: RegisztracioAllapot; id: string }[],
  letezoProfil: boolean,
  slugFoglalt: boolean,
): string[] {
  const f: string[] = [];
  if (tobbi.some((t) => t.id !== r.id && t.adoszam === r.adoszam && (t.allapot === 'fuggo' || t.allapot === 'jovahagyva'))) {
    f.push('Ezzel az adószámmal már van függő vagy jóváhagyott regisztráció.');
  }
  if (letezoProfil) f.push('A kapcsolattartónak már van fiókja.');
  if (slugFoglalt) f.push('Ez a slug már foglalt.');
  return f;
}

/** A meghívás állapotának szövege a részletező oldalon. */
export function meghivasSzoveg(allapot: string | null, hiba: string | null): string {
  switch (allapot) {
    case 'meghivva': return 'A meghívó elküldve a kapcsolattartónak.';
    case 'hozzaadva': return 'A kapcsolattartó már létező felhasználó, hozzáadtuk az irodához vezetőként.';
    case 'ujraaktivalva': return 'A kapcsolattartó korábbi tagságát újraaktiváltuk vezetőként.';
    case 'hiba': return `A meghívó küldése nem sikerült${hiba ? ` (${hiba})` : ''}.`;
    default: return 'A meghívás állapota nem ismert.';
  }
}

/** Újraküldhető-e a meghívás: hibás vagy sosem rögzített eredménynél igen. */
export const meghivasUjrakuldheto = (allapot: string | null): boolean => allapot === null || allapot === 'hiba';
